import { CommonModule } from '@angular/common';
import { Component, OnChanges, OnInit, SimpleChanges, TemplateRef, computed, inject, input, output, signal, viewChild } from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { ClientApi, Client } from '@venecia/apis/identity';
import { ErrorMessageService, MessageService, TraceService } from '@venecia/services';

import { ResponsiveTable } from '../responsive-table/responsive-table';
import type { Column } from '../responsive-table/responsive-table.model';
import type { RowAction } from '../responsive-table/responsive-table.model';
import { Spinner } from '../spinner/spinner';

@Component({
  selector: 'app-client-grid',
  standalone: true,
  imports: [CommonModule, TranslateModule, ResponsiveTable, Spinner],
  templateUrl: './client-grid.html',
  styleUrl: './client-grid.scss',
})
export class ClientGrid implements OnInit, OnChanges {
  private readonly clientApi = inject(ClientApi);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly messageService = inject(MessageService);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);

  readonly accountId = input<number | null>(null);
  readonly reloadVersion = input<number>(0);
  readonly detailRequested = output<Client>();
  readonly totalChanged = output<number>();

  readonly clients = signal<readonly Client[]>([]);
  readonly loading = signal(false);

  // Pagination (server-side)
  readonly pageNumber = signal(1); // 1-based
  readonly pageSize = signal(10);
  readonly totalItems = signal(0);
  readonly statusCell = viewChild<TemplateRef<{ $implicit: Client }>>('statusCell');
  readonly trackByClient = (_: number, c: Client) => c.id ?? c['client-id'];

  readonly columns = computed<readonly Column<Client>[]>(() => [
    {
      id: 'client-id',
      header: this.translateService.instant('pages.clients.fields.client-id'),
      value: (c) => c['client-id'],
      nowrap: true,
    },
    {
      id: 'type',
      header: this.translateService.instant('pages.clients.fields.type'),
      value: (c) => c.type,
    },
    {
      id: 'active',
      header: this.translateService.instant('pages.clients.fields.active'),
      cellTemplate: this.statusCell(),
      nowrap: true,
    },
  ]);

  readonly actions = computed<readonly RowAction<Client>[]>(() => [
    {
      id: 'details',
      label: this.translateService.instant('pages.clients.actions.details'),
      icon: 'fa-solid fa-pen-to-square',
    },
  ]);

  ngOnInit(): void {
    void this.loadClients();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const reloadVersionChange = changes['reloadVersion'];
    if (!reloadVersionChange || reloadVersionChange.firstChange) {
      return;
    }

    this.pageNumber.set(1);
    void this.loadClients();
  }

  onActionSelected(ev: { actionId: string; item: Client }): void {
    if (ev.actionId === 'details') {
      this.detailRequested.emit(ev.item);
    }
  }

  onPageChange(page: number): void {
    if (page === this.pageNumber()) {
      return;
    }

    this.pageNumber.set(page);
    void this.loadClients();
  }

  private async loadClients(): Promise<void> {
    this.loading.set(true);

    try {
      const response = await firstValueFrom(
        this.clientApi.search({
          payload: this.buildSearchPayload(),
        })
      );

      this.clients.set(response.body.data ?? []);
      this.totalItems.set(response.body.total ?? 0);
      this.totalChanged.emit(this.totalItems());
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.clients.load-error'));
    } finally {
      this.loading.set(false);
    }
  }

  private buildSearchPayload(): Record<string, unknown> {
    const accountId = this.accountId();

    return {
      page: this.pageNumber() - 1,
      size: this.pageSize(),
      ...(accountId !== null
        ? {
            filters: [
              {
                field: 'account.id',
                value: accountId,
                comparative: 'eq',
              },
            ],
          }
        : {}),
    };
  }
}
